import {
  forwardRef,
  useImperativeHandle,
  useRef,
  type VideoHTMLAttributes,
} from 'react';
import YouTubeVideo, {
  type YouTubePlayer,
  type YouTubeProps as YtProps,
} from 'react-youtube';

function extractVideoId(url: string) {
  if (!url.includes('www.youtube.com')) throw new Error('Invalid YouTube URL');

  const { pathname, searchParams } = new URL(url);

  switch (true) {
    case pathname === '/watch':
      return searchParams.get('v');

    case pathname.startsWith('/embed/'):
      return pathname.split('/').at(2) ?? null;

    default:
      return null;
  }
}

export type YouTubeProps = Omit<
  VideoHTMLAttributes<HTMLVideoElement>,
  'children'
> &
  Pick<YtProps, 'videoId'>;

export type YouTubeRef = YouTubePlayer | null | undefined;

export const YouTube = forwardRef<YouTubeRef, YouTubeProps>(
  (props, forwardedRef) => {
    const youtubeRef = useRef<YouTubeVideo>(null);

    const instance = youtubeRef.current?.getInternalPlayer();

    const videoId = props.videoId ?? (props.src && extractVideoId(props.src));

    useImperativeHandle<YouTubeRef, YouTubeRef>(forwardedRef, () => instance);

    return (
      <YouTubeVideo
        ref={youtubeRef}
        className={props.className}
        iframeClassName="tlw-size-full"
        videoId={videoId as string}
        opts={{
          playerVars: {
            autoplay: props.autoPlay ? 1 : 0,
            controls: 1,
            loop: props.loop ? 1 : 0,
            iv_load_policy: 3, // 影片註釋，1:顯示(默認)，3:不顯示
            playsinline: 1, // 在iOS的播放器中全屏播放，0:全屏(默認)，1:內嵌
            rel: 0, // 顯示相關影片，0:不顯示，1:顯示(默認)
          },
        }}
        onReady={(e) => {
          props.muted && e.target.mute();
          props.autoPlay && e.target.playVideo();
        }}
      />
    );
  }
);
